import { FunctionHelper, StateProvider } from "@bryntum/schedulerpro";

const persistenceDelay = 500;

export default class SwivuStateProvider extends StateProvider {
  static get $name() {
    return "SwivuStateProvider";
  }

  getValue(key) {
    this.writeStatefuls.flush();

    let value = this.storage.getItem(this.prefix + key);

    if (value) {
      value = JSON.parse(value);
    }

    return value;
  }

  setValue(key, value) {
    const { storage } = this,
      // Normally `was` is a current state. Considering it is only
      // used in the event, I assume it should be safe to skip it.
      was = null;

    key = this.prefix + key;

    if (value != null) {
      if (value || typeof value !== "string") {
        value = JSON.stringify(value);
      }

      storage.setItem(key, value);

      this.trigger("set", { key, value, was });
    } else if (was !== null) {
      storage.removeItem(key);

      this.trigger("remove", { key, was });
    }

    return this;
  }
}

class PersistedStatesStorage {
  key = "bryntum-state:backend-storage";
  localKey = "bryntum-state:localStoragePersistence";
  swivuClientsViewKey = "bryntum-state:b-client-view-tasks-state";
  localStoragePersitedStateKeys = [
    "scroll",
    "subGrids",
    "style"
  ];

  constructor(user, store) {
    this.user = user;
    this.store = store;
    const record = this.store.find((r) => r.key === this.key);

    if (!record) {
      const { id } = user;
      const newRecord = this.store.createRecord({
        key: this.key,
        value: JSON.stringify({}),
        user: id
      });
      this.store.add(newRecord);
      this.data = {};
      this.record = newRecord;
    } else {
      this.data = JSON.parse(record.value);
      this.record = record;
    }

    this.doSync = FunctionHelper.createBuffered(() => {
      this.record.value = this.value;
    }, persistenceDelay);
  }

  getItem(key) {
    const currentBryntumComponentValue = this.data[key]
      ? JSON.parse(this.data[key])
      : {};
    const oldLocalValue = JSON.parse(localStorage.getItem(this.localKey));
    let result = currentBryntumComponentValue;
    if (oldLocalValue) {
      const currentBryntumComponentOldLocalValue = oldLocalValue[key];

      if (currentBryntumComponentOldLocalValue) {
        if (key !== this.swivuClientsViewKey) {
          const localFlexStyle = currentBryntumComponentOldLocalValue.style;
          const serverStyles = result.style;
          const style = `${serverStyles ?? ""} ${localFlexStyle}`;
          currentBryntumComponentOldLocalValue[style] = style;
        }
        result = { ...result, ...currentBryntumComponentOldLocalValue };
      }
    }
    result = this.filterState(result);
    return JSON.stringify(result);
  }

  /**
   * We may want to not persist some properties of component so this function
   * is used to delete the unwanted ones.
   * At present, it deletes rowHeight as that depends on fontSize and compactView
   * and is calculated by the runtime logic as should not be saved in the state.
   * @param {Object} state
   * @returns Object
   */
  filterState(state) {
    delete state.rowHeight;
    return state;
  }

  getLocalAndServerStorageStyles(styles, property) {
    const items = [...styles.match(/([\w-]+)\s*:\s*([^;]+)\s*;?/g)];
    const propertyIdx = items.findIndex((i) => i.includes(property));
    const localPropertyValue = items[propertyIdx];
    items[propertyIdx] = "";
    const serverPropertyValues = items.join("");
    return [localPropertyValue, serverPropertyValues];
  }

  getStyleObj(value) {
    const style = {};
    value.split(";").forEach(el => {
      const [property, value] = el.split(":");
      if (!property) return;
      style[property.trim()] = value.trim();
    });
    return style;
  }

  setItem(key, value) {
    value = JSON.parse(value);
    let localStoragePersist = {
      [key]: {}
    };
    for (const stateKey in value) {
      if (this.localStoragePersitedStateKeys.includes(stateKey)) {
        if (stateKey === "style") {
          if (value[stateKey]["localStoragePersistedStyles"]) {
            Object.entries(this.getStyleObj(value[stateKey]["localStoragePersistedStyles"])).forEach(([styleKey, styleValue]) => {
              localStoragePersist[key][styleKey] = styleValue;
            });
          } else {
            const [localFlexStyle, serverStyles] =
              this.getLocalAndServerStorageStyles(value[stateKey], "flex");
            localStoragePersist[key][stateKey] = localFlexStyle;
            value[stateKey] = serverStyles;
          }
        }
        else {
          localStoragePersist[key][stateKey] = value[stateKey];
          delete value[stateKey];
        }
      }
    }
    if (localStorage.getItem(this.localKey)) {
      const oldLocalValue = JSON.parse(localStorage.getItem(this.localKey));
      localStorage.setItem(
        this.localKey,
        JSON.stringify({ ...oldLocalValue, ...localStoragePersist })
      );
    } else {
      localStorage.setItem(this.localKey, JSON.stringify(localStoragePersist));
    }
    this.data[key] = JSON.stringify(value);
    this.value = JSON.stringify(this.data);
    this.doSync();
  }
  removeItem(key) {
    delete this.data[key];
    this.record.value = JSON.stringify(this.data);
  }

  clear() {
    this.data = {};
    this.store.remove(this.record);
  }
}

export function initPersistedStorage(user, store) {
  return new PersistedStatesStorage(user, store);
}
